import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  MdDownload,
  MdOutlineCalendarMonth,
  MdOutlineArticle,
  MdOutlinePerson,
  MdOutlineLocationOn,
  MdOutlineAttachFile,
  MdLink,
  MdOutlineCategory,
  MdPeople,
  MdEmail,
  MdComment,
  MdLanguage,
  MdEdit,
  MdDelete,
  MdAdd,
  MdMoreVert,
} from "react-icons/md";

const ArticleDetails = ({
  article,
  getArticleTitle,
  getArticleAuthor,
  getPhotoUrl,
  onEditArticle,
  onDeleteArticle,
  onAddDocument,
  onEditDocument,
  onDeleteDocument,
}) => {
  const [activeTab, setActiveTab] = useState("overview");
  const [selectedLanguage, setSelectedLanguage] = useState("fr");
  const [showActionsMenu, setShowActionsMenu] = useState(false);

  // Fonction pour obtenir le titre selon la langue sélectionnée
  const getTitle = () => {
    if (!article.title_art) return "Titre non disponible";
    if (typeof article.title_art === "string") return article.title_art;
    
    // Si c'est un objet multilingue
    if (typeof article.title_art === "object") {
      return article.title_art[selectedLanguage] || 
             article.title_art.fr || 
             article.title_art.en || 
             article.title_art.mg || 
             "Titre non disponible";
    }
    
    return getArticleTitle(article);
  };

  const submissionDate = article.date_soum_art
    ? new Date(article.date_soum_art).toLocaleDateString()
    : "Non spécifié";
  const publicationDate = article.date_pub_art
    ? new Date(article.date_pub_art).toLocaleDateString()
    : "Non spécifié";
  const studyArea = article.study_area_art || "Non spécifié";
  const rubriques = article.rubriques || [];
  const coauthors = article.coauthors || [];
  const reviewers = article.reviewers || [];

  // Fonctions pour obtenir le résumé selon la langue sélectionnée
  const getSummary = (lang) => {
    if (!article.resume_art) return null;
    if (typeof article.resume_art === "string") return article.resume_art;
    
    // Si c'est un objet multilingue
    if (typeof article.resume_art === "object") {
      const summary = article.resume_art[lang];
      if (typeof summary === "string") return summary;
      if (summary && typeof summary === "object") {
        return JSON.stringify(summary);
      }
    }
    return null;
  };

  // Fonction pour obtenir les mots-clés selon la langue sélectionnée
  const getKeywords = (lang) => {
    if (!article.key_words_art) return [];
    if (Array.isArray(article.key_words_art)) return article.key_words_art;
    
    // Si c'est un objet multilingue
    if (typeof article.key_words_art === "object") {
      return article.key_words_art[lang] || [];
    }
    return [];
  };

  // Fonction pour obtenir le contenu selon la langue sélectionnée
  const getContent = (lang) => {
    if (!article.corp_art) return null;
    if (typeof article.corp_art === "string") return article.corp_art;
    
    // Si c'est un objet multilingue
    if (typeof article.corp_art === "object") {
      const content = article.corp_art[lang];
      if (typeof content === "string") return content;
      if (content && typeof content === "object") {
        return JSON.stringify(content);
      }
    }
    return null;
  };

  const hasAttachedFile = Boolean(article.file_art);
  const hasCoverPhoto = Boolean(article.photo_art);
  const documents = article.documents || [];
  const commentaires = article.commentaires || [];

  // Fonction pour obtenir le nom de la langue
  const getLanguageName = (lang) => {
    const languages = {
      fr: "Français",
      en: "English", 
      mg: "Malagasy"
    };
    return languages[lang] || lang.toUpperCase();
  };

  // Fonction pour obtenir le drapeau de la langue
  const getLanguageFlag = (lang) => {
    const flags = {
      fr: "🇫🇷",
      en: "🇺🇸",
      mg: "🇲🇬"
    };
    return flags[lang] || "🌍";
  };

  // Vérifier quelles langues ont du contenu
  const getAvailableLanguages = () => {
    const languages = [];
    
    if (article.title_art?.fr || article.resume_art?.fr || article.key_words_art?.fr?.length > 0) {
      languages.push("fr");
    }
    if (article.title_art?.en || article.resume_art?.en || article.key_words_art?.en?.length > 0) {
      languages.push("en");
    }
    if (article.title_art?.mg || article.resume_art?.mg || article.key_words_art?.mg?.length > 0) {
      languages.push("mg");
    }
    
    return languages.length > 0 ? languages : ["fr"]; // Fallback sur français
  };

  const availableLanguages = getAvailableLanguages();

  // Fonction pour formater la date des commentaires
  const formatCommentDate = (dateString) => {
    if (!dateString) return "Date inconnue";
    const date = new Date(dateString);
    return date.toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Fonction pour obtenir le nom du statut
  const getStatusName = (statusId) => {
    const statusMap = {
      1: "En attente",
      2: "À remanier",
      3: "Publiable",
      4: "En révision",
      5: "Publié",
      6: "Suspendu",
      7: "Refusé",
      8: "En évaluation",
    };
    return statusMap[statusId] || `Statut ${statusId}`;
  };

  // Fonction pour obtenir la couleur du statut
  const getStatusColor = (statusId) => {
    const colorMap = {
      1: "bg-gray-100 text-gray-800",
      2: "bg-yellow-100 text-yellow-800",
      3: "bg-green-100 text-green-800",
      4: "bg-blue-100 text-blue-800",
      5: "bg-emerald-100 text-emerald-800",
      6: "bg-orange-100 text-orange-800",
      7: "bg-red-100 text-red-800",
      8: "bg-purple-100 text-purple-800",
    };
    return colorMap[statusId] || "bg-gray-100 text-gray-800";
  };

  const tabs = [
    { id: "overview", label: "Résumé", icon: MdOutlineArticle },
    { id: "authors", label: "Auteurs", icon: MdOutlinePerson },
    { id: "reviewers", label: "Évaluateurs", icon: MdPeople },
    { id: "comments", label: "Commentaires", icon: MdComment },
    { id: "documents", label: "Documents", icon: MdOutlineAttachFile },
  ];

  return (
    <div className="h-full overflow-hidden rounded-xl">
      <div className="h-full">
        <div className="grid h-full grid-cols-1 md:grid-cols-4">
          {/* Scientific sidebar with metadata */}
          <div className="border-r border-gray-100 bg-gray-50/70 p-6 md:col-span-1">
            <div className="space-y-6">
              {/* Language Selector */}
              {availableLanguages.length > 1 && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Langue"}
                  </h3>
                  <div className="ml-3">
                    <div className="flex items-center gap-2 mb-2">
                      <MdLanguage className="h-4 w-4 text-[#57A863]" />
                      <span className="text-sm font-medium text-[#57A863]">
                        {availableLanguages.length} langue{availableLanguages.length > 1 ? 's' : ''} disponible{availableLanguages.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {availableLanguages.map((lang) => (
                        <button
                          key={`lang-${lang}`}
                          onClick={() => setSelectedLanguage(lang)}
                          className={`flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs font-medium transition-colors ${
                            selectedLanguage === lang
                              ? "bg-[#57A863] text-white"
                              : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                          }`}
                          title={`Afficher en ${getLanguageName(lang)}`}
                        >
                          <span>{getLanguageFlag(lang)}</span>
                          <span>{lang.toUpperCase()}</span>
                        </button>
                      ))}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Langue actuelle: {getLanguageName(selectedLanguage)}
                    </div>
                  </div>
                </div>
              )}

              {/* Article type */}
              <div>
                <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                  {"Type de document"}
                </h3>
                <p className="ml-3 text-sm font-medium">
                  {article.type_art || "Article scientifique"}
                </p>
              </div>

              {/* Publication info */}
              <div>
                <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                  {"Dates"}
                </h3>
                <div className="ml-3 space-y-3">
                  <div className="flex items-start gap-2">
                    <MdOutlineCalendarMonth className="mt-0.5 h-4 w-4 text-[#57A863]" />
                    <div>
                      <p className="text-xs text-gray-500">
                        {"Date de soumission"}
                      </p>
                      <p className="text-sm font-medium">{submissionDate}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <MdOutlineCalendarMonth className="mt-0.5 h-4 w-4 text-[#57A863]" />
                    <div>
                      <p className="text-xs text-gray-500">
                        {"Date de publication"}
                      </p>
                      <p className="text-sm font-medium">{publicationDate}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Comments count in sidebar */}
              {commentaires.length > 0 && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Historique"}
                  </h3>
                  <div className="ml-3">
                    <div className="mb-2 flex items-center gap-2">
                      <MdComment className="h-4 w-4 text-[#57A863]" />
                      <span className="text-sm font-medium text-[#57A863]">
                        {commentaires.length} commentaire
                        {commentaires.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {commentaires.slice(0, 2).map((comment, index) => (
                        <div
                          key={`sidebar-comment-${comment.id_comm}-${index}`}
                          className="text-xs text-gray-600"
                        >
                          <p className="font-medium">
                            {comment.user?.first_name} {comment.user?.last_name}
                          </p>
                          <p className="text-gray-500">
                            {formatCommentDate(comment.created_at)}
                          </p>
                        </div>
                      ))}
                      {commentaires.length > 2 && (
                        <p className="text-xs italic text-gray-500">
                          +{commentaires.length - 2} autre
                          {commentaires.length - 2 > 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Reviewers in sidebar */}
              {reviewers.length > 0 && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Évaluateurs"}
                  </h3>
                  <div className="ml-3">
                    <div className="mb-2 flex items-center gap-2">
                      <MdPeople className="h-4 w-4 text-[#57A863]" />
                      <span className="text-sm font-medium text-[#57A863]">
                        {reviewers.length} évaluateur
                        {reviewers.length > 1 ? "s" : ""}
                      </span>
                    </div>
                    <div className="space-y-1">
                      {reviewers.slice(0, 3).map((reviewer, index) => (
                        <p
                          key={`sidebar-reviewer-${reviewer.id_rev}-${index}`}
                          className="text-xs text-gray-600"
                        >
                          {reviewer.grade_rev} {reviewer.first_name}{" "}
                          {reviewer.last_name}
                        </p>
                      ))}
                      {reviewers.length > 3 && (
                        <p className="text-xs italic text-gray-500">
                          +{reviewers.length - 3} autre
                          {reviewers.length - 3 > 1 ? "s" : ""}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* DOI */}
              {article.doi_art && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Identifiant"}
                  </h3>
                  <div className="ml-3">
                    <a
                      href={`https://doi.org/${article.doi_art}`}
                      target="_blank"
                      rel="noreferrer"
                      className="group flex items-center gap-1.5 text-sm font-medium text-[#57A863] hover:text-[#4c9755]"
                    >
                      <MdLink className="h-4 w-4" />
                      <span className="break-all group-hover:underline">
                        {"DOI: "}
                        {article.doi_art}
                      </span>
                    </a>
                  </div>
                </div>
              )}

              {/* Study Area */}
              <div>
                <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                  {"Zone d'étude"}
                </h3>
                <div className="ml-3 flex items-start gap-2">
                  <MdOutlineLocationOn className="mt-0.5 h-4 w-4 text-[#57A863]" />
                  <p className="text-sm font-medium">{studyArea}</p>
                </div>
              </div>

              {/* Rubriques */}
              {rubriques.length > 0 && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Rubriques"}
                  </h3>
                  <div className="ml-3">
                    <MdOutlineCategory className="mb-2 h-4 w-4 text-[#57A863]" />
                    <div className="flex flex-wrap gap-1.5">
                      {rubriques.map((rubrique) => (
                        <span
                          key={`rubrique-${rubrique.id_rub}`}
                          className="rounded-full bg-[#57A86315] px-2.5 py-1 text-xs text-[#57A863]"
                        >
                          {rubrique.name_rub}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Cover image */}
              {hasCoverPhoto && (
                <div>
                  <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                    <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                    {"Image"}
                  </h3>
                  <div className="ml-3 overflow-hidden rounded-lg border border-gray-200">
                    <img
                      src={getPhotoUrl(`/${article.photo_art}`)}
                      alt="Couverture de l'article"
                      className="h-auto w-full object-cover"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Main content area */}
          <div className="flex h-full flex-col md:col-span-3">
            {/* Header with title and actions */}
            <div className="border-b border-gray-100 px-6 py-5">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <h1 className="text-xl font-bold text-gray-800 md:text-2xl">
                      {getTitle()}
                    </h1>
                    {availableLanguages.length > 1 && (
                      <span className="flex items-center gap-1 rounded-md bg-[#57A86315] px-2 py-1 text-xs font-medium text-[#57A863]">
                        {getLanguageFlag(selectedLanguage)}
                        {selectedLanguage.toUpperCase()}
                      </span>
                    )}
                  </div>
                  <p className="mt-1 font-medium text-gray-600">
                    {getArticleAuthor(article)}
                  </p>
                </div>
                
                {/* Actions menu */}
                <div className="flex items-center gap-2">
                  {/* Main download button */}
                  {hasAttachedFile && (
                    <a
                      href={getPhotoUrl(`/${article.file_art}`)}
                      target="_blank"
                      rel="noreferrer"
                      title="Télécharger l'article"
                      className="flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#57A863] to-[#4c9755] px-4 py-2.5 font-medium text-white transition-all hover:shadow-md"
                    >
                      <MdDownload className="h-5 w-5" />
                    </a>
                  )}

                  {/* Actions dropdown */}
                  <div className="relative">
                    <button
                      onClick={() => setShowActionsMenu(!showActionsMenu)}
                      className="flex items-center justify-center rounded-full border border-gray-300 bg-white p-2.5 text-gray-600 transition-all hover:bg-gray-50 hover:text-gray-800"
                      title="Actions"
                    >
                      <MdMoreVert className="h-5 w-5" />
                    </button>

                    {/* Dropdown menu */}
                    {showActionsMenu && (
                      <div className="absolute right-0 z-10 mt-2 w-48 rounded-lg border border-gray-200 bg-white shadow-lg">
                        <div className="py-1">
                          <button
                            onClick={() => {
                              onEditArticle();
                              setShowActionsMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                          >
                            <MdEdit className="h-4 w-4" />
                            Modifier l'article
                          </button>
                          {/*<button
                            onClick={() => {
                              onDeleteArticle();
                              setShowActionsMenu(false);
                            }}
                            className="flex w-full items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                          >
                            <MdDelete className="h-4 w-4" />
                            Supprimer l'article
                          </button> */}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="border-b border-gray-100 bg-white px-6">
              <div className="-mb-px flex">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`inline-flex items-center border-b-2 px-4 py-3 text-sm font-medium ${
                        activeTab === tab.id
                          ? "border-[#57A863] text-[#57A863]"
                          : "border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700"
                      }`}
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {tab.label}
                      {/* Badge pour le nombre d'évaluateurs */}
                      {tab.id === "reviewers" && reviewers.length > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center rounded-full bg-[#57A863] px-2 py-0.5 text-xs font-medium text-white">
                          {reviewers.length}
                        </span>
                      )}
                      {/* Badge pour le nombre de commentaires */}
                      {tab.id === "comments" && commentaires.length > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center rounded-full bg-[#57A863] px-2 py-0.5 text-xs font-medium text-white">
                          {commentaires.length}
                        </span>
                      )}
                      {/* Badge pour le nombre de documents */}
                      {tab.id === "documents" && documents.length > 0 && (
                        <span className="ml-2 inline-flex items-center justify-center rounded-full bg-[#57A863] px-2 py-0.5 text-xs font-medium text-white">
                          {documents.length}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Tab Content */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Overview Tab */}
              {activeTab === "overview" && (
                <div>
                  {/* Indicateur de langue si multiple */}
                  {availableLanguages.length > 1 && (
                    <div className="mb-6 rounded-lg bg-[#57A86310] p-4">
                      <div className="flex items-center gap-2">
                        <MdLanguage className="h-5 w-5 text-[#57A863]" />
                        <span className="text-sm font-medium text-[#57A863]">
                          Contenu affiché en {getLanguageName(selectedLanguage)}
                        </span>
                        <div className="ml-auto flex gap-1">
                          {availableLanguages.map((lang) => (
                            <button
                              key={`header-lang-${lang}`}
                              onClick={() => setSelectedLanguage(lang)}
                              className={`flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors ${
                                selectedLanguage === lang
                                  ? "bg-[#57A863] text-white"
                                  : "bg-white text-gray-600 hover:bg-gray-100"
                              }`}
                            >
                              {getLanguageFlag(lang)}
                              {lang.toUpperCase()}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Résumé selon la langue sélectionnée */}
                  <div className="space-y-8">
                    {getSummary(selectedLanguage) && (
                      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-5">
                        <h2 className="mb-3 flex items-center text-lg font-semibold">
                          <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                          {selectedLanguage === "fr" && "Résumé"}
                          {selectedLanguage === "en" && "Abstract"}
                          {selectedLanguage === "mg" && "Famintinana"}
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({getLanguageName(selectedLanguage)})
                          </span>
                        </h2>
                        <p className="leading-relaxed text-gray-700">
                          {getSummary(selectedLanguage)}
                        </p>
                      </div>
                    )}

                    {/* Contenu de l'article si disponible */}
                    {getContent(selectedLanguage) && (
                      <div className="rounded-xl border border-gray-100 bg-gray-50/50 p-5">
                        <h2 className="mb-3 flex items-center text-lg font-semibold">
                          <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                          Contenu
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({getLanguageName(selectedLanguage)})
                          </span>
                        </h2>
                        <div 
                          className="prose prose-sm max-w-none leading-relaxed text-gray-700"
                          dangerouslySetInnerHTML={{ __html: getContent(selectedLanguage) }}
                        />
                      </div>
                    )}
                  </div>

                  {/* Rubriques display in overview tab */}
                  {rubriques.length > 0 && (
                    <div className="mt-8">
                      <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                        {"Catégories"}
                      </h3>
                      <div className="ml-3 mt-2 flex flex-wrap gap-2">
                        {rubriques.map((rubrique) => (
                          <div
                            key={`overview-rubrique-${rubrique.id_rub}`}
                            className="flex items-center rounded-lg border border-gray-200 bg-white p-2 shadow-sm"
                          >
                            {rubrique.photo_rub && (
                              <img
                                src={getPhotoUrl(`/${rubrique.photo_rub}`)}
                                alt={rubrique.name_rub}
                                className="mr-2 h-5 w-5 rounded object-cover"
                              />
                            )}
                            <span className="text-sm font-medium">
                              {rubrique.name_rub}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Keywords selon la langue sélectionnée */}
                  {getKeywords(selectedLanguage).length > 0 && (
                    <div className="mt-8">
                      <h3 className="mb-2 flex items-center text-xs font-semibold uppercase tracking-wide text-gray-500">
                        <span className="mr-2 inline-block h-4 w-1 rounded-full bg-[#57A863]"></span>
                        {"Mots-clés"}
                      </h3>
                      <div className="ml-3">
                        <div className="mb-3">
                          <p className="mb-1.5 text-xs text-gray-500">
                            {getLanguageName(selectedLanguage)}
                          </p>
                          <div className="flex flex-wrap gap-1.5">
                            {getKeywords(selectedLanguage).map((keyword, idx) => (
                              <span
                                key={`${selectedLanguage}-${keyword}-${idx}`}
                                className="rounded-full bg-[#57A86315] px-2.5 py-1 text-xs text-[#57A863]"
                              >
                                {keyword}
                              </span>
                            ))}
                          </div>
                        </div>

                        {/* Afficher aussi les mots-clés des autres langues s'ils existent */}
                        {availableLanguages.length > 1 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <p className="text-xs text-gray-500 mb-2">Autres langues :</p>
                            <div className="space-y-2">
                              {availableLanguages
                                .filter(lang => lang !== selectedLanguage && getKeywords(lang).length > 0)
                                .map(lang => (
                                  <div key={`other-lang-${lang}`}>
                                    <p className="mb-1 text-xs text-gray-500">
                                      {getLanguageFlag(lang)} {getLanguageName(lang)}
                                    </p>
                                    <div className="flex flex-wrap gap-1">
                                      {getKeywords(lang).slice(0, 5).map((keyword, idx) => (
                                        <span
                                          key={`${lang}-${keyword}-${idx}`}
                                          className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600"
                                        >
                                          {keyword}
                                        </span>
                                      ))}
                                      {getKeywords(lang).length > 5 && (
                                        <span className="text-xs text-gray-400">
                                          +{getKeywords(lang).length - 5} autres
                                        </span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Authors Tab */}
              {activeTab === "authors" && (
                <div>
                  <h2 className="mb-5 flex items-center text-lg font-semibold">
                    <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                    {"Liste des auteurs"}
                  </h2>
                  <div className="space-y-6">
                    {/* Main author */}
                    {article.author && (
                      <div className="rounded-xl border-l-4 border-[#57A863] bg-[#57A86310] p-4">
                        <div className="mb-2 flex items-center">
                          <span className="inline-flex items-center rounded-full bg-[#57A863] px-2.5 py-0.5 text-xs font-medium text-white">
                            {"Auteur principal"}
                          </span>
                        </div>
                        <h3 className="text-base font-medium">
                          {article.author.title_aut} {article.author.first_name}{" "}
                          {article.author.last_name}
                        </h3>
                        <p className="mt-1 text-sm text-gray-600">
                          {article.author.email_user}
                        </p>
                        {article.author.affiliations &&
                          article.author.affiliations.length > 0 && (
                            <p className="mt-1 text-sm italic text-gray-500">
                              {article.author.affiliations[0].name_aff}
                            </p>
                          )}
                      </div>
                    )}

                    {/* Co-authors */}
                    {coauthors.length > 0 && (
                      <div className="mt-6">
                        <h3 className="mb-4 flex items-center text-base font-medium">
                          <span className="mr-2 inline-block h-4 w-1.5 rounded-full bg-[#57A863]"></span>
                          {"Co-auteurs"} ({coauthors.length})
                        </h3>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                          {coauthors.map((coauthor, index) => (
                            <div
                              key={`coauthor-${
                                coauthor.email_user || coauthor.last_name
                              }-${index}`}
                              className="rounded-xl border border-gray-100 bg-gray-50/50 p-4 transition-shadow hover:shadow-sm"
                            >
                              <h4 className="text-sm font-medium">
                                {coauthor.title_aut} {coauthor.first_name}{" "}
                                {coauthor.last_name}
                              </h4>
                              <p className="mt-1 text-sm text-gray-600">
                                {coauthor.email_user}
                              </p>
                              {coauthor.affiliations &&
                                coauthor.affiliations.length > 0 && (
                                  <p className="mt-1 text-sm italic text-gray-500">
                                    {coauthor.affiliations[0].name_aff}
                                  </p>
                                )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Reviewers Tab */}
              {activeTab === "reviewers" && (
                <div>
                  <h2 className="mb-5 flex items-center text-lg font-semibold">
                    <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                    {"Évaluateurs assignés"}
                  </h2>
                  {reviewers.length > 0 ? (
                    <div className="space-y-4">
                      <div className="mb-6 rounded-lg bg-[#57A86310] p-4">
                        <div className="flex items-center gap-2">
                          <MdPeople className="h-5 w-5 text-[#57A863]" />
                          <span className="text-sm font-medium text-[#57A863]">
                            {reviewers.length} évaluateur
                            {reviewers.length > 1 ? "s" : ""} assigné
                            {reviewers.length > 1 ? "s" : ""} à cet article
                          </span>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
                        {reviewers.map((reviewer, index) => (
                          <div
                            key={`reviewer-${reviewer.id_rev}-${index}`}
                            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm transition-all hover:shadow-md"
                          >
                            {/* Avatar et grade */}
                            <div className="mb-3 flex items-center gap-3">
                              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#57A863] to-[#4a9154] font-semibold text-white">
                                {reviewer.first_name
                                  ?.charAt(0)
                                  ?.toUpperCase() || ""}
                                {reviewer.last_name?.charAt(0)?.toUpperCase() ||
                                  ""}
                              </div>
                              <div>
                                <span className="inline-flex items-center rounded-full bg-[#57A863] px-2 py-0.5 text-xs font-medium text-white">
                                  {reviewer.grade_rev || "Évaluateur"}
                                </span>
                              </div>
                            </div>

                            {/* Nom complet */}
                            <h4 className="text-base font-semibold text-gray-900">
                              {reviewer.grade_rev} {reviewer.first_name}{" "}
                              {reviewer.last_name}
                            </h4>

                            {/* Email */}
                            <div className="mt-2 flex items-center gap-2">
                              <MdEmail className="h-4 w-4 text-gray-400" />
                              <p className="break-all text-sm text-gray-600">
                                {reviewer.email_user}
                              </p>
                            </div>

                            {/* ID utilisateur */}
                            <div className="mt-2">
                              <p className="text-xs text-gray-500">
                                ID: {reviewer.id_user}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-10 text-center">
                      <MdPeople className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-3 text-sm font-medium text-gray-500">
                        {"Aucun évaluateur assigné"}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {
                          "Cet article n'a pas encore été assigné à des évaluateurs"
                        }
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Comments Tab */}
              {activeTab === "comments" && (
                <div>
                  <h2 className="mb-5 flex items-center text-lg font-semibold">
                    <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                    {"Historique des commentaires"}
                  </h2>
                  {commentaires.length > 0 ? (
                    <div className="space-y-4">
                      <div className="mb-6 rounded-lg bg-[#57A86310] p-4">
                        <div className="flex items-center gap-2">
                          <MdComment className="h-5 w-5 text-[#57A863]" />
                          <span className="text-sm font-medium text-[#57A863]">
                            {commentaires.length} commentaire
                            {commentaires.length > 1 ? "s" : ""}
                          </span>
                        </div>
                      </div>

                      <div className="space-y-4">
                        {commentaires.map((comment) => (
                          <div
                            key={`comment-${comment.id_comm}`}
                            className="rounded-xl border border-gray-100 bg-white p-4 shadow-sm"
                          >
                            {/* User info and timestamp */}
                            <div className="mb-3 flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-[#57A863] to-[#4a9154] font-semibold text-white">
                                  {comment.user?.first_name
                                    ?.charAt(0)
                                    ?.toUpperCase() || ""}
                                  {comment.user?.last_name
                                    ?.charAt(0)
                                    ?.toUpperCase() || ""}
                                </div>
                                <div>
                                  <h4 className="text-sm font-medium">
                                    {comment.user?.first_name}{" "}
                                    {comment.user?.last_name}
                                  </h4>
                                  <p className="text-xs text-gray-500">
                                    {comment.user?.email_user}
                                  </p>
                                </div>
                              </div>
                              <time className="text-xs text-gray-500">
                                {formatCommentDate(comment.created_at)}
                              </time>
                            </div>

                            {/* Comment content */}
                            <div className="ml-13">
                              <p className="text-sm text-gray-700">
                                {comment.content_comm}
                              </p>

                              {/* Status badges */}
                              <div className="mt-3 flex flex-wrap gap-2">
                                {comment.id_stat && (
                                  <span
                                    className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(
                                      comment.id_stat
                                    )}`}
                                  >
                                    {getStatusName(comment.id_stat)}
                                  </span>
                                )}
                                {comment.id_stat_interne && (
                                  <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-0.5 text-xs font-medium text-gray-800">
                                    Status interne: {comment.id_stat_interne}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-10 text-center">
                      <MdComment className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-3 text-sm font-medium text-gray-500">
                        {"Aucun commentaire"}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {"Cet article n'a pas encore reçu de commentaires"}
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* Documents Tab - SUITE ET FIN */}
              {activeTab === "documents" && (
                <div>
                  <div className="mb-5 flex items-center justify-between">
                    <h2 className="flex items-center text-lg font-semibold">
                      <span className="mr-3 inline-block h-5 w-2 rounded-full bg-[#57A863]"></span>
                      {"Documents annexes"}
                    </h2>
                    <button
                      onClick={onAddDocument}
                      className="flex items-center gap-2 rounded-lg bg-[#57A863] px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-[#4c9755]"
                    >
                      <MdAdd className="h-4 w-4" />
                      Ajouter un document
                    </button>
                  </div>

                  {documents.length > 0 ? (
                    <div className="space-y-3">
                      {documents.map((doc, index) => (
                        <div
                          key={`doc-${doc.id_doc || doc.title_doc || index}`}
                          className="rounded-xl border border-gray-100 p-4 transition-colors hover:bg-gray-50/80"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900">
                                {doc.title_doc}
                              </h4>
                              <p className="mt-1 text-xs text-gray-500">
                                {doc.type_doc} • {doc.auth_doc}
                              </p>
                              
                              {/* Description du document */}
                              {doc.description_doc && (
                                <p className="mt-2 text-xs text-gray-600">
                                  {doc.description_doc}
                                </p>
                              )}

                              {/* Informations sur la source */}
                              {doc.source_doc && (
                                <div className="mt-3">
                                  <p className="text-xs text-gray-500">
                                    {"Date: "}
                                    {doc.source_doc.date || "N/A"}
                                  </p>
                                  <div className="mt-2 flex flex-wrap gap-3">
                                    {doc.source_doc.url1 && (
                                      <a
                                        href={doc.source_doc.url1}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1 text-xs text-[#57A863] hover:underline"
                                      >
                                        <MdLink className="h-3 w-3" />
                                        {"Source 1"}
                                      </a>
                                    )}
                                    {doc.source_doc.url2 && (
                                      <a
                                        href={doc.source_doc.url2}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="flex items-center gap-1 text-xs text-[#57A863] hover:underline"
                                      >
                                        <MdLink className="h-3 w-3" />
                                        {"Source 2"}
                                      </a>
                                    )}
                                  </div>
                                </div>
                              )}

                              {/* Taille du fichier si disponible */}
                              {doc.file_size && (
                                <p className="mt-2 text-xs text-gray-500">
                                  Taille: {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              )}

                              {/* Date de création/modification */}
                              {doc.created_at && (
                                <p className="mt-1 text-xs text-gray-500">
                                  Ajouté le: {new Date(doc.created_at).toLocaleDateString("fr-FR")}
                                </p>
                              )}
                            </div>
                            
                            {/* Actions pour chaque document */}
                            <div className="flex items-center gap-2">
                              {/* Bouton de téléchargement */}
                              <a
                                href={getPhotoUrl(`/${doc.path_doc}`)}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-full bg-[#57A863] p-2 text-white transition-colors hover:bg-[#4c9755]"
                                title="Télécharger"
                              >
                                <MdDownload className="h-4 w-4" />
                              </a>
                              
                              {/* Bouton d'édition */}
                              <button
                                onClick={() => onEditDocument(doc.id_doc)}
                                className="rounded-full bg-blue-600 p-2 text-white transition-colors hover:bg-blue-700"
                                title="Modifier"
                              >
                                <MdEdit className="h-4 w-4" />
                              </button>
                              
                              {/* Bouton de suppression */}
                              <button
                                onClick={() => onDeleteDocument(doc.id_doc, doc.title_doc)}
                                className="rounded-full bg-red-600 p-2 text-white transition-colors hover:bg-red-700"
                                title="Supprimer"
                              >
                                <MdDelete className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-xl border-2 border-dashed border-gray-200 bg-gray-50/50 py-10 text-center">
                      <MdOutlineAttachFile className="mx-auto h-12 w-12 text-gray-300" />
                      <p className="mt-3 text-sm font-medium text-gray-500">
                        {"Aucun document associé à cet article"}
                      </p>
                      <p className="mt-1 text-xs text-gray-400">
                        {"Cliquez sur 'Ajouter un document' pour commencer"}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Click outside handler pour fermer le menu actions */}
      {showActionsMenu && (
        <div
          className="fixed inset-0 z-5"
          onClick={() => setShowActionsMenu(false)}
        />
      )}
    </div>
  );
};

ArticleDetails.propTypes = {
  article: PropTypes.shape({
    id_art: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title_art: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        fr: PropTypes.string,
        en: PropTypes.string,
        mg: PropTypes.string,
      }),
    ]),
    resume_art: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        fr: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        en: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
        mg: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
      }),
    ]),
    key_words_art: PropTypes.oneOfType([
      PropTypes.array,
      PropTypes.shape({
        fr: PropTypes.array,
        en: PropTypes.array,
        mg: PropTypes.array,
      }),
    ]),
    corp_art: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        fr: PropTypes.string,
        en: PropTypes.string,
        mg: PropTypes.string,
      }),
    ]),
    date_soum_art: PropTypes.string,
    date_pub_art: PropTypes.string,
    study_area_art: PropTypes.string,
    type_art: PropTypes.string,
    coauthors: PropTypes.array,
    rubriques: PropTypes.array,
    reviewers: PropTypes.array,
    commentaires: PropTypes.arrayOf(
      PropTypes.shape({
        id_comm: PropTypes.number,
        content_comm: PropTypes.string,
        id_stat_interne: PropTypes.number,
        id_stat: PropTypes.number,
        created_at: PropTypes.string,
        updated_at: PropTypes.string,
        user: PropTypes.shape({
          id: PropTypes.number,
          first_name: PropTypes.string,
          last_name: PropTypes.string,
          email_user: PropTypes.string,
          id_role: PropTypes.number,
        }),
      })
    ),
    file_art: PropTypes.string,
    photo_art: PropTypes.string,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        id_doc: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        title_doc: PropTypes.string,
        type_doc: PropTypes.string,
        auth_doc: PropTypes.string,
        path_doc: PropTypes.string,
        description_doc: PropTypes.string,
        file_size: PropTypes.number,
        created_at: PropTypes.string,
        source_doc: PropTypes.shape({
          date: PropTypes.string,
          url1: PropTypes.string,
          url2: PropTypes.string,
        }),
      })
    ),
    doi_art: PropTypes.string,
    author: PropTypes.object,
  }).isRequired,
  getArticleTitle: PropTypes.func.isRequired,
  getArticleAuthor: PropTypes.func.isRequired,
  getPhotoUrl: PropTypes.func.isRequired,
  onEditArticle: PropTypes.func.isRequired,
  onDeleteArticle: PropTypes.func.isRequired,
  onAddDocument: PropTypes.func.isRequired,
  onEditDocument: PropTypes.func.isRequired,
  onDeleteDocument: PropTypes.func.isRequired,
};

export default ArticleDetails;